import React, { useEffect, useRef, useState } from "react";
import useApi from "../../services/useApi";
import endpoints from "../../services/endpoints";
import Image from "../images/Image";
import moment from "moment";

interface Notification {
  user_id: string;
  read: boolean;
  timestamp: string;
  ttl: number;
  title: string;
  body: string;
  type: string;
  metadata?: {
    amount?: number;
    user_code?: string;
    requested_from_user_code?: string;
    requested_from_user?: string;
    user_name?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  bellIconRef?: React.RefObject<HTMLDivElement>;
  onUnreadStatusChange?: (hasUnread: boolean) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  bellIconRef,
  onUnreadStatusChange,
}) => {
  const { getAPI, PutAPI } = useApi();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextEvaluatedKey, setNextEvaluatedKey] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [scrollHeight, setScrollHeight] = useState<string>('350px');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef<boolean>(false);

  // Set responsive scroll height and detect mobile
  useEffect(() => {
    const updateResponsive = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      if (mobile) {
        setScrollHeight('calc(100vh - 180px)');
      } else {
        setScrollHeight('350px');
      }
    };

    updateResponsive();
    window.addEventListener('resize', updateResponsive);
    return () => window.removeEventListener('resize', updateResponsive);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Reset state when opening dropdown
      setNotifications([]);
      setNextEvaluatedKey(null);
      fetchNotifications();
      
      // Prevent body scroll on mobile when dropdown is open
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      }
      
      // Remove red dot indicator immediately when dropdown opens
      onUnreadStatusChange?.(false);
      
      // Mark that dropdown was open
      wasOpenRef.current = true;
    } else {
      // Restore body scroll when dropdown closes
      document.body.style.overflow = '';
      
      // Only mark all notifications as read if dropdown was previously open
      // This prevents calling the API on initial mount when isOpen is false
      if (wasOpenRef.current) {
        PutAPI(endpoints.markAllNotificationsRead)
          .catch(error => console.error("Error marking notifications as read:", error));
        wasOpenRef.current = false;
      }
    }

    return () => {
      // Cleanup: restore body scroll
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile, onUnreadStatusChange]);

  const fetchNotifications = async (nextKey?: string | null) => {
    if (nextKey) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const limit = 10;
      const endpoint = nextKey 
        ? `${endpoints.notifications}?limit=${limit}&next_evaluated_key=${encodeURIComponent(nextKey)}`
        : `${endpoints.notifications}?limit=${limit}`;
      
      const response = await getAPI(endpoint);
      if (!response.error && response.data) {
        // API returns { data: [], count: number, next_evaluated_key: string }
        const notificationData = response.data.data || [];
        
        if (nextKey) {
          // Append new notifications for pagination
          setNotifications(prev => [...prev, ...notificationData]);
        } else {
          // Replace notifications for initial load
          setNotifications(notificationData);
        }
        
        // Set next_evaluated_key if available
        setNextEvaluatedKey(response.data.next_evaluated_key || null);
      } else {
        if (!nextKey) {
          setNotifications([]);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      if (!nextKey) {
        setNotifications([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    
    const now = moment();
    const date = moment(dateString);
    const diffHours = now.diff(date, "hours");

    if (diffHours < 1) {
      const diffMinutes = now.diff(date, "minutes");
      return diffMinutes < 1 ? "Just now" : `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return date.format("MMM DD");
    }
  };

  // Handle scroll for infinite pagination
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollPosition = Math.ceil(target.scrollTop + target.clientHeight);
    const threshold = target.scrollHeight - 20; // 20px before the end

    if (
      scrollPosition >= threshold &&
      nextEvaluatedKey &&
      !loadingMore &&
      !loading
    ) {
      fetchNotifications(nextEvaluatedKey);
    }
  };

  // Handle click outside - exclude the notification bell icon
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      // Don't close if clicking on the bell icon (handled by toggle in parent)
      if (bellIconRef?.current?.contains(target)) {
        return;
      }
      // Close if clicking outside the dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose, bellIconRef]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black/30 z-99"
          onClick={onClose}
        />
      )}
      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed top-20 left-1/2 transform -translate-x-1/2 w-[calc(100vw-24px)] max-w-[calc(100vw-24px)]'
            : 'absolute right-0 sm:right-[-30px] mt-2 sm:mt-6 w-[400px] md:w-[500px]'
        } bg-white border border-s_dark_hover rounded-lg shadow-lg z-100 flex flex-col pr-1`}
        style={{ maxHeight: isMobile ? 'calc(100vh - 120px)' : '500px' }}
      >
        <div className="px-4 sm:px-4 py-3 border-b border-gray-200 shrink-0 flex items-center justify-between">
          <h3 className="text-heading5 font-semibold text-primary_text">
            Notifications
          </h3>
          {isMobile && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-hidden"
              aria-label="Close notifications"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

      <div 
        ref={scrollContainerRef}
        className="overflow-y-auto scrollBar" 
        style={{ height: scrollHeight }}
        onScroll={handleScroll}
      >
        {loading && notifications.length === 0 ? (
          <div className="p-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-p_primary"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-6 sm:p-8 flex flex-col items-center justify-center min-h-[300px]">
            <Image src="not-found" className="w-[120px] h-[96px] sm:w-[149px] sm:h-[120px]" />
            <p className="text-body1 text-primary_text mt-4 text-[0.875rem] sm:text-[1rem] text-center px-4">
              No notifications yet
            </p>
          </div>
        ) : (
          <div>
            {notifications.map((notification, index) => (
              <div
                key={`${notification.user_id}-${notification.timestamp}-${index}`}
                className="px-4 sm:px-4 py-3 sm:py-3 transition-colors flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4"
              >
                <p className={`text-body2 leading-[22px] sm:leading-[22px] flex-1 ${
                  !notification.read ? 'text-p_primary font-medium' : 'text-primary_text'
                }`}>
                  {notification.body}
                </p>
                <p className={`text-body2 whitespace-nowrap shrink-0 ${
                  !notification.read ? 'text-p_primary' : 'text-secondary_text'
                }`}>
                  {formatDate(notification.timestamp)}
                </p>
              </div>
            ))}
            {loadingMore && (
              <div className="p-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-p_primary"></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default NotificationDropdown;

